/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  swapProgrammedItemOnChildCalendar,
  setCombineWithInfo,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const alreadySavedGlobal = new Set();

const WarmupTable = ({
  contentIndex,
  content,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const [isAdding, setIsAdding] = useState(false);
  const programType = useSelector((state) => state.calendar.data.programType);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const formatDate = () => `${moment(date).format('YYYY-MM-DD')}`;
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleAutoSaveCalendarWarmup = (contentIndexParam) => {
    setIsAdding(true);
    const submissionObject = {
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(date).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: 'warm_up',
      showSessionBreakItems: false,
      order: contentIndex,
    };
    if (content.description) {
      submissionObject.description = content.description;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(date).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {},
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {},
        content.assignedId,
      ));
    }
  };

  /** the CellMeasurer wrapped around each row in the virtualized list
    * can cause remounting of the item a few times as it measures
    * the "just in time" height, this can cause multiple firings of this
    * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
    * To get around that we save the item by id
    * in the global set that will persist renders and if we have alreay saved
    * the item them we will not fire that function again.
    */

  useEffect(() => {
    if (content.preSave && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarWarmup(contentIndex);
    }
  }, [content.assignedId]);

  /** This useEffect listens for changes is the exercise chosen from the
    * table dropdown menu
    */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      setIsAdding(true);
      const updatedItem = {
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        type: 'warm_up',
        order: contentIndex,
      };
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }
      if (programType === 'free-form-program') {
        const reorder = false;
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {
              setIsAdding(false);
            },
            onError: () => {
              setIsAdding(false);
            },
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          const reorder = false;
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        } else {
          const reorder = false;
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        }
      }
    }
  }, [swapDropdownSelectedExercise, nonSwappedItemFromParentCalendar]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (!content.preSave && combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      setIsAdding(true);
      const updatedItem = {
        ...content,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: content.exerciseId,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,
        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
        type: 'warm_up',
        order: contentIndex,
      };
      const reorder = true;
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }

      if (programType === 'free-form-program') {
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {
              setIsAdding(false);
            },
            onError: () => {
              setIsAdding(false);
            },
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        } else {
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {
                setIsAdding(false);
              },
              onError: () => {
                setIsAdding(false);
              },
            },
          ));
        }
      }
    }
  }, [combineWithInfo, nonSwappedItemFromParentCalendar]);

  return (
    <Container />
  );
};

WarmupTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

WarmupTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default WarmupTable;
